import events from '../../../events/bus'

import { SDK_AUTH_POPUP_OPEN, SDK_AUTH_POPUP_CLOSE, SDK_POPUP_BLOCKED } from '../../../events/types/sdk.events'
import { WEB_MESSAGE_RESPONSE_TYPE, ORIGINS_ALLOWED_LIST } from '../constants'
import { PostMessageResponse, SessionState } from '../constants/types'
import { SDK_CLOSE_POPUP_EVENT, SPACE } from '../../../constants'
import { AuthenticationCanceled } from '../../../errors'

type HandlerResponseResolver = (event: MessageEvent<PostMessageResponse>) => void
type HandlerResponseRejector = (error?: Error | string) => void

type PopupWindowConfig = {
  readonly location: string,
  readonly toolbar: string,
  readonly rel?: string,
  readonly width: number,
  readonly height: number,
  readonly left: number,
  readonly top: number,
  readonly modal?: string,
}



export class Popup {
  public readonly params: string
  public window: Window | null = null
  protected isLoading = false

  public static get DEFAULT_CONFIG(): PopupWindowConfig {
    return {
      location: 'no',
      toolbar: 'no',
      width: 500,
      height: 780,
      left: 100,
      top: 100,
      modal: 'yes'
    }
  }

  constructor(config: PopupWindowConfig = Popup.DEFAULT_CONFIG, open: boolean, dark = true) {
    this.params = Object.keys(config).map(key => `${key}=${config[key as never]}`).join(',')
    if (open) this.create(dark)

    window.addEventListener('beforeunload', () => {
      this.close && this.close()
      return SPACE
    }, { once: true })
  }

  create(dark = false, url?: string) {
    if (!this.window) {
      this.window = window.open(SPACE || url, '_blank', this.params) as Window
      if (dark) this.window.document.body.style.backgroundColor = '#151515'
      events.dispatchEvent(SDK_AUTH_POPUP_OPEN)
    }

    return this
  }

  close() {
    this.isLoading = false
    this.window && this.window.close()
    if (this.window?.closed) events.dispatchEvent(SDK_AUTH_POPUP_CLOSE)

    return this
  }

  open(url: string, id: string, dark = false): Promise<SessionState> {
    this.isLoading = false
    if (!this.window) this.create(dark, url)

    return new Promise((resolve, reject) => {
      if (this.window && !this.window.closed) {
        const handler = (event: MessageEvent<PostMessageResponse>) => {
          const { data, origin } = event
          const isOIDCPostMessage = data && data.type === WEB_MESSAGE_RESPONSE_TYPE && data.response.state === id
          if (isOIDCPostMessage) {
            timer && clearInterval(timer as never)
            window.removeEventListener('message', handler)
            resolve({ ...data.response, origin })
          }
        }

        window.addEventListener('message', handler)

        this.window.focus()
        this.window.location.href = url

        const timer = this.watchPopupClose(handler, reject)
      } else {
        events.dispatchEvent(SDK_POPUP_BLOCKED)
        reject()
      }
    })
  }

  public focus() {
    window.focus()
    this.window?.focus()
    return this
  }

  protected watchPopupClose(handler: HandlerResponseResolver, _reject: HandlerResponseRejector): NodeJS.Timer {
    const timer = setInterval(() => {
      if (!this.window || (this.window && this.window.closed)) {
        clearInterval(timer)

        window.removeEventListener('message', handler, false)
        events.dispatchEvent(SDK_AUTH_POPUP_CLOSE)

        _reject(new AuthenticationCanceled)
      }
    }, 500)

    return timer
  }
}

export type Config = {
  cdn: string,
  service: string
}

export class PopupWindow extends Popup {
  private promocode?: string
  private urlPath?: string
  private query: string
  private config: Config

  constructor(confg: Config, promocode?: string, urlPath?: string, query = SPACE) {
    super(Popup.DEFAULT_CONFIG, false, true)
    this.promocode = promocode
    this.urlPath = urlPath
    this.query = query
    this.config = confg
  }

  public showLoader(dark = Boolean(window?.matchMedia('(prefers-color-scheme: dark)')?.matches), cdn?: string) {
    const cdnUrl = (this.config.cdn || cdn)
    if (this.window && cdnUrl) {
      const v = Math.round(Math.random() * 1000000)
      const LOADER_PAGE = cdnUrl + '/loader/index.html'
      this.isLoading = true
      this.window.location.href = LOADER_PAGE + `?v=${v}` + (dark ? '&ui=dark' : SPACE)
    }

    return this
  }

  get path(): string {
    const query = (typeof this.query === 'string') && this.query ? '?' + this.query : SPACE

    switch (true) {
      case this.urlPath && typeof this.urlPath === 'string':
        return `${this.config.service}${this.urlPath?.startsWith('/') ? this.urlPath + query : '/' + this.urlPath}${query}`
      case this.promocode && typeof this.promocode === 'string':
        return `${this.config.service}/checkout/promo?code=${encodeURIComponent(this.promocode as string)}${ this.query ? '&' + this.query : SPACE}`
      default:
        return this.config.service + query
    }
  }

  public openSubscriptionService(options?: { promocode?: string, url?: string, query: string }) {
    return new Promise((resolve, reject) => {
      if (options) {
        if (options.promocode) this.promocode = options.promocode
        if (options.url) this.urlPath = options.url
        if (options.query) this.query = options.query
      }

      const handler = (event: MessageEvent) =>
        (event && event.data === SDK_CLOSE_POPUP_EVENT && ORIGINS_ALLOWED_LIST.includes(event.origin as string)) && resolve(undefined)

      window.removeEventListener('message', handler, false)
      window.addEventListener('message', handler, false)

      if (this.window) {
        this.window.location.href = this.path
      } else {
        this.open(this.path, '').catch(reject).then(resolve)
      }

      this.watchPopupClose(handler, reject)
    })
  }
}
